/**
 * Pure helpers for the "Assign installation to imports" step (backend
 * docs/features/import-installation-backfill, P2). The step lists the
 * supplier-only import lines a new emission factor covers, grouped by
 * reporting period, so the declarant can assign the installation to them.
 */

/** Stable key for one import line. */
export const lineKey = (line) => `${line.shipmentId}:${line.contentId}`;

/**
 * Lines in a period whose CBAM report was already generated start unticked
 * (D4/D11): assigning them would make the data diverge from that report.
 */
export const initialSelection = (lines) =>
  new Set(lines.filter((line) => !line.reportGenerated).map(lineKey));

/**
 * Group lines by reporting period ("2025-Q4" quarterly, "2026" annual), in
 * period order. Each group knows whether its report was generated.
 */
export const groupByPeriod = (lines) => {
  const groups = new Map();
  for (const line of lines) {
    if (!groups.has(line.periodKey)) {
      const [year, quarter] = line.periodKey.split('-');
      groups.set(line.periodKey, {
        key: line.periodKey,
        year,
        quarter: quarter ?? null,
        reportGenerated: false,
        lines: [],
      });
    }
    const group = groups.get(line.periodKey);
    group.lines.push(line);
    if (line.reportGenerated) group.reportGenerated = true;
  }
  return [...groups.values()].sort((a, b) => a.key.localeCompare(b.key));
};

/** 'all' | 'none' | 'partial' for a set of lines against the selection. */
export const selectionState = (lines, selected) => {
  const count = lines.filter((line) => selected.has(lineKey(line))).length;
  if (count === 0) return 'none';
  return count === lines.length ? 'all' : 'partial';
};

/**
 * Toggle a set of lines together: select them all unless they already all
 * are, in which case clear them. Returns a new Set.
 */
export const toggleLines = (lines, selected) => {
  const next = new Set(selected);
  const allSelected = lines.every((line) => next.has(lineKey(line)));
  for (const line of lines) {
    if (allSelected) next.delete(lineKey(line));
    else next.add(lineKey(line));
  }
  return next;
};

/**
 * The parent checkbox: from nothing selected it picks every line not in a
 * reported period (the default) — or every line, when all of them are in
 * reported periods; otherwise it clears everything.
 */
export const toggleAll = (lines, selected) => {
  if (selected.size > 0) return new Set();
  const defaults = initialSelection(lines);
  return defaults.size > 0 ? defaults : new Set(lines.map(lineKey));
};

// Import line units are MeasurementUnit codes (backend customsProcedureData.ts).
const UNIT_LABELS = { '01': 't', '02': 'kg', '03': 'MWh', '04': 'm³', '05': 'GJ' };
const KILOGRAMS = '02';

/** Short label for a line's unit code. */
export const unitLabel = (unit) => UNIT_LABELS[String(unit)] ?? '';

/**
 * Change in embedded emissions (tCO₂e) for one line if it switches to
 * `newTotal`. Factors are per tonne for goods measured by mass, so kilograms
 * are converted; other units (MWh, m³, GJ) take the factor per that unit.
 */
export const lineDelta = (line, newTotal) => {
  const current = Number(line.current?.total);
  const next = Number(newTotal);
  const quantity = Number(line.quantity);
  if (![current, next, quantity].every(Number.isFinite)) return null;
  const amount = String(line.unit) === KILOGRAMS ? quantity / 1000 : quantity;
  return (next - current) * amount;
};

/** The request body for POST /emissions/:id/assign-imports. */
export const selectedLinesPayload = (lines, selected) =>
  lines
    .filter((line) => selected.has(lineKey(line)))
    .map(({ shipmentId, contentId }) => ({ shipmentId, contentId }));

/**
 * Whether assigning changes the line's country of origin: the installation's country
 * replaces it. Unknown on either side is no change to flag.
 */
export const changesCountry = (line, installationCountry) =>
  line.countryOfOrigin != null &&
  installationCountry != null &&
  String(line.countryOfOrigin) !== String(installationCountry);
